import React, { useState, useContext, useEffect } from "react";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import AlertModal from "../../components/AlertModal";
import { SkeletonStyles, SkeletonBox, LoadingPill } from "../../components/SkeletonLoader";

import text from "./constants/reportsTranslations";
import { getCategories } from "./constants/reportsCategories";
import { translateText } from "./utils/reportsHelpers";
import { getResponsiveStyles } from "./style/reportsStyles";

import useScreenSize from "./hooks/useScreenSize";
import useReportsData from "./hooks/useReportsData";
import useEmailModal from "./hooks/useEmailModal";
import useReportsDashboardData from "./hooks/useReportsDashboardData";
import useRecentReports from "./hooks/useRecentReports";
import useReportInsights from "./hooks/useReportInsights";
import useReportFilters from "./hooks/useReportFilters";
import useReportTable from "./hooks/useReportTable";
import useScheduledReports from "./hooks/useScheduledReports";
import useReportExport from "./hooks/useReportExport";
import useReportFilterOptions from "./hooks/useReportFilterOptions";

import EnhancedEmailModal from "./components/EnhancedEmailModal";
import ReportsHeader from "./components/header/ReportsHeader";
import KpiCardsRow from "./components/kpi/KpiCardsRow";
import ReportsList from "./components/categories/ReportsList";
import RecentReportsSection from "./components/recent/RecentReportsSection";
import InsightsSection from "./components/insights/InsightsSection";
import FiltersPanel from "./components/filters/FiltersPanel";
import ReportPreviewPage from "./components/preview/ReportPreviewPage";
import ScheduleReportModal from "./components/export/ScheduleReportModal";
import EmptyState from "./components/states/EmptyState";

const Reports = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const isRTL = language === "ar";
  const isDark = theme === "dark";

  const { screenSize } = useScreenSize();
  const { currentUser, reportsLoading, canDownloadReports } = useReportsData(language);
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => { if (!reportsLoading) setPageReady(true); }, [reportsLoading]);

  const categories = getCategories(text, language);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const selectedCategory = categories.find((c) => c.id === selectedCategoryId) || null;

  const { kpis, kpisLoading, refetchKpis } = useReportsDashboardData(currentUser);
  const { recent, recentLoading, refetchRecent } = useRecentReports(currentUser);
  const { insights, insightsLoading, refetchInsights } = useReportInsights(currentUser);
  const { downloadReport, exportRowsToCsv, exportRowsToPdf } = useReportExport(currentUser);
  const { createSchedule } = useScheduledReports(currentUser);
  const { departments, employees, companies, managers, optionsLoading } = useReportFilterOptions(currentUser);
  const filterOptions = { departments, employees, companies, managers };

  const { draft, setField, apply, reset, requestFilters } = useReportFilters(selectedCategoryId);
  const { rows, summary, title: tableTitle, tableLoading, tableError, refetchTable } = useReportTable(currentUser, selectedCategoryId, requestFilters);

  // Which Detailed Data columns are currently shown on screen — kept in
  // sync from DataTable's own Columns toggle so the PDF/Excel export never
  // includes a column the user just hid. Resets when the category changes
  // (DataTable also remounts then, via its `key`).
  const [visibleColumns, setVisibleColumns] = useState(null);
  useEffect(() => { setVisibleColumns(null); }, [selectedCategoryId]);

  const { showEmailModal, setShowEmailModal, selectedReport, setSelectedReport, emailLoading, emailStatus, setEmailStatus, emailForm, setEmailForm, handleShareReport } = useEmailModal(language, currentUser);
  const styles = getResponsiveStyles(theme, language, screenSize, "grid", emailLoading, emailStatus);

  const [alertModal, setAlertModal] = useState({ open: false, title: "", message: "", variant: "info" });
  const [scheduleModal, setScheduleModal] = useState({ open: false, categoryId: null });
  const closeAlert = () => setAlertModal((prev) => ({ ...prev, open: false }));

  const showError = (message) => setAlertModal({ open: true, title: `✗ ${text[language].error}`, message, variant: "error" });
  const showSuccess = (message) => setAlertModal({ open: true, title: `✓ ${text[language].success}`, message, variant: "success" });

  // handleShareReport (useEmailModal) still reports its own result via
  // emailStatus — route it through the same AlertModal instead of the old
  // StatusAlert toast banner, then clear it so it can't double-fire.
  useEffect(() => {
    if (!emailStatus.message) return;
    if (emailStatus.type === "error") showError(emailStatus.message);
    else showSuccess(emailStatus.message);
    setEmailStatus({ type: "", message: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailStatus]);

  const guardedDownload = async (type, format, filters) => {
    if (!canDownloadReports) { showError(text[language].errorDownloadingReport); return; }
    try {
      await downloadReport(type, format, filters);
      refetchRecent();
      showSuccess(text[language].reportDownloadSuccess);
    } catch (e) {
      showError(e.message);
    }
  };

  const openEmailModalFor = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    // Carry the currently applied filters along with the report so the
    // emailed PDF/Excel is scoped the same as the on-screen preview/table —
    // without this, useEmailModal had no way to know what filters were
    // active and always emailed the full, unfiltered report.
    setSelectedReport({ category: categoryId, title: category?.name || categoryId, description: category?.description || "", filters: requestFilters });
    setEmailForm({
      recipients: [], subject: `${text[language].shareReport}: ${category?.name}`,
      message: text[language].addPersonalMessage, includeAttachment: true, format: "pdf",
    });
    setShowEmailModal(true);
  };

  const refreshAll = () => { refetchKpis(); refetchRecent(); refetchInsights(); if (selectedCategoryId) refetchTable(); };

  if (!pageReady && reportsLoading) {
    return (
      <div>
        <SkeletonStyles theme={theme} />
        <LoadingPill text={text[language].loadingReports} theme={theme} isRTL={isRTL} />
        <div style={{ background: isDark ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)", padding: 32, borderRadius: 24, marginBottom: 22 }}>
          <SkeletonBox width="220px" height="30px" style={{ marginBottom: 12 }} />
          <SkeletonBox width="320px" height="18px" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(150px, 100%), 1fr))", gap: 20, marginBottom: 24 }}>
          {[1, 2, 3, 4, 5].map((i) => <SkeletonBox key={i} width="100%" height="118px" style={{ borderRadius: 16 }} />)}
        </div>
        <SkeletonBox width="100%" height="360px" style={{ borderRadius: 18 }} />
      </div>
    );
  }

  return (
    <div>
      <EnhancedEmailModal
        showEmailModal={showEmailModal} selectedReport={selectedReport} emailForm={emailForm} setEmailForm={setEmailForm}
        emailLoading={emailLoading} onClose={() => setShowEmailModal(false)} handleShareReport={handleShareReport}
        styles={styles} screenSize={screenSize} language={language} text={text} theme={theme}
        canDownloadReports={canDownloadReports} translateText={(t, cat) => translateText(t, cat, language, text)}
        employeeOptions={employees} employeeOptionsLoading={optionsLoading}
      />

      <ScheduleReportModal
        open={scheduleModal.open}
        reportType={scheduleModal.categoryId}
        onClose={() => setScheduleModal({ open: false, categoryId: null })}
        onCreate={async (payload) => {
          await createSchedule(payload);
          showSuccess(text[language].scheduleSuccess);
        }}
      />

      <ReportsHeader theme={theme} isRTL={isRTL} text={text} language={language} onRefresh={refreshAll} refreshing={kpisLoading} />

      <KpiCardsRow kpis={kpis} kpisLoading={kpisLoading} text={text} language={language} screenSize={screenSize} />
      {/* <RecentReportsSection recent={recent} recentLoading={recentLoading} onOpenReport={setSelectedCategoryId} /> */}

      {/* Two-panel workspace: left = report list, right = filters + inline preview. Selecting a report never navigates away.
          NOTE: sticky positioning temporarily removed from the filter bar — it was interacting badly with the
          portal-rendered filter dropdowns while scrolling (dropdown staying anchored to a stale position). Will
          re-add once that's isolated properly; for now the filter bar scrolls with the page like everything else. */}
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", marginBottom: 24 }}>
        <div style={{ width: 240, flexShrink: 0 }}>
          <ReportsList categories={categories} selectedCategoryId={selectedCategoryId} onSelect={setSelectedCategoryId} theme={theme} isRTL={isRTL} />
        </div>

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
          {!selectedCategory ? (
            <EmptyState theme={theme} isRTL={isRTL} title={text[language].emptyStateTitle} description={text[language].emptyStateDesc} />
          ) : (
            <>
              <FiltersPanel
                category={selectedCategory} draft={draft} setField={setField} apply={apply} reset={reset}
                options={filterOptions} optionsLoading={optionsLoading}
              />

              <div
                style={{
                  borderRadius: 18, padding: 20,
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
                  background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
                  boxShadow: isDark ? "0 8px 26px rgba(0,0,0,0.25)" : "0 8px 26px rgba(15,23,42,0.06)",
                }}
              >
                <ReportPreviewPage
                  category={selectedCategory} rows={rows} summary={summary} title={tableTitle}
                  tableLoading={tableLoading} tableError={tableError} onRetry={refetchTable}
                  onExcel={() => guardedDownload(selectedCategoryId, "xlsx", { ...requestFilters, columns: visibleColumns || undefined })}
                  onPdf={() => guardedDownload(selectedCategoryId, "pdf", { ...requestFilters, columns: visibleColumns || undefined })}
                  onEmail={() => openEmailModalFor(selectedCategoryId)}
                  onSchedule={() => setScheduleModal({ open: true, categoryId: selectedCategoryId })}
                  onExportSelected={(selected, format) => (format === "pdf"
                    ? exportRowsToPdf(selected, `${selectedCategoryId}-selected`, tableTitle)
                    : exportRowsToCsv(selected, `${selectedCategoryId}-selected`)
                  ).catch((e) => showError(e.message))}
                  onVisibleColumnsChange={setVisibleColumns}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* <InsightsSection insights={insights} insightsLoading={insightsLoading} /> */}

      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} variant={alertModal.variant} onClose={closeAlert} theme={theme === "dark" ? "dark" : "light"} language={language} />
    </div>
  );
};

export default Reports;
